// ./src/utils/api/EventMessageOverrideService.ts
//
// Event message-override service (ROADMAP A3 — pre/post-action messages). OpResult-native. Host-authored
// per-event copy chosen by context: pre-action form copy, post-action website copy, and a REPLACEMENT of a
// narrative sender template. Every list is filtered by eventId. A row is keyed by (surface, messageTrigger,
// statusKey, channel); the selectors are set on create and immutable. Full single-item CRUD + bulk remove.
// See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// GraphQL
// =============================================================================

const OVERRIDE_FIELDS = `
  id
  eventId
  accountId
  surface
  messageTrigger
  statusKey
  channel
  subject
  bodyHtml
  bodyText
  bodySms
  isEnabled
  rights
  createdBy
  createdAt
  updatedAt
`;

const OVERRIDE_LIST_QUERY = gql`
  query EventMessageOverrideList($filter: EventMessageOverrideFilter, $params: PaginationAndSorting) {
    eventMessageOverrideList(filter: $filter, params: $params) {
      code
      data { ${OVERRIDE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const OVERRIDE_CREATE_MUTATION = gql`
  mutation EventMessageOverrideCreate($params: EventMessageOverrideInput) {
    eventMessageOverrideCreate(params: $params) {
      code
      data { ${OVERRIDE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const OVERRIDE_UPDATE_MUTATION = gql`
  mutation EventMessageOverrideUpdate($where: EventMessageOverrideWhereInput, $params: EventMessageOverrideInput) {
    eventMessageOverrideUpdate(where: $where, params: $params) {
      code
      data { ${OVERRIDE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const OVERRIDE_REMOVE_MUTATION = gql`
  mutation EventMessageOverrideRemove($where: EventMessageOverrideWhereInput) {
    eventMessageOverrideRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api EventMessageOverride schema)
// =============================================================================

export const MESSAGE_OVERRIDE_SURFACE = {
  FORM_PRE: "form_pre",
  SITE_POST: "site_post",
  MESSAGE: "message",
} as const;

export type MessageOverrideSurface = (typeof MESSAGE_OVERRIDE_SURFACE)[keyof typeof MESSAGE_OVERRIDE_SURFACE];

export interface EventMessageOverride {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  surface?: string | null;
  messageTrigger?: string | null;
  statusKey?: string | null;
  channel?: string | null;
  subject?: string | null;
  bodyHtml?: string | null;
  bodyText?: string | null;
  bodySms?: string | null;
  isEnabled?: boolean | null;
  rights?: string[] | null;
  createdBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface EventMessageOverrideInput {
  eventId?: string | null;
  surface?: string | null;
  messageTrigger?: string | null;
  statusKey?: string | null;
  channel?: string | null;
  subject?: string | null;
  bodyHtml?: string | null;
  bodyText?: string | null;
  bodySms?: string | null;
  isEnabled?: boolean | null;
}

export interface EventMessageOverrideFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  surface?: string[] | null;
  messageTrigger?: string[] | null;
  statusKey?: string[] | null;
  channel?: string[] | null;
}

export interface EventMessageOverrideWhereInput {
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

export class EventMessageOverrideService {
  static async list(filter?: EventMessageOverrideFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(OVERRIDE_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async create(params: EventMessageOverrideInput): Promise<OpResult> {
    return ApiService.executeMutation(OVERRIDE_CREATE_MUTATION, { params });
  }

  static async update(where: EventMessageOverrideWhereInput, params: EventMessageOverrideInput): Promise<OpResult> {
    return ApiService.executeMutation(OVERRIDE_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(OVERRIDE_REMOVE_MUTATION, { where: { id } });
  }
}
